/**
 * Dashboard core translations: nav, emailVerification, impersonation, account, loading, empty, sepa, invoice, common
 */

export const nav = {
  legal: {
    ariaLabel: 'Legal information',
    imprint: 'Imprint',
    privacy: 'Privacy',
    terms: 'Terms',
    withdrawal: 'Withdrawal',
    cancellation: 'Cancel'
  },
  logoAlt: 'ITSH - Go to homepage',
  roles: {
    admin: 'Administrator',
    reseller: 'Reseller',
    customer: 'Customer'
  },
  viewSwitchLabel: 'Switch view',
  viewUser: 'User',
  viewAdmin: 'Admin',
  viewHintAdmin: 'You are viewing admin functions',
  viewHintUser: 'You are viewing the customer view',
  sections: {
    shop: 'Shop',
    administration: 'Administration',
    customerServices: 'Customer Services',
    myServices: 'My Services',
    finance: 'Finance',
    account: 'Account'
  },
  adminGroups: {
    core: 'Core',
    data: 'Data',
    finance: 'Finance',
    system: 'System'
  },
  items: {
    dashboard: 'Dashboard',
    products: 'Products',
    cart: 'Cart',
    users: 'Users',
    invoices: 'Invoices',
    featureFlags: 'Feature Flags',
    migration: 'Migration',
    accountSplit: 'Account Splits',
    compliance: 'Compliance',
    allDomains: 'All Domains',
    allHandles: 'All Handles',
    featuredTlds: 'Featured TLDs',
    fulfillment: 'Fulfilment',
    webhooks: 'Webhooks',
    email: 'Email',
    domains: 'Domains',
    packages: 'Packages',
    withdraw: 'Withdraw from contract',
    account: 'Account',
    help: 'Help',
    support: 'Support',
    logout: 'Log out',
    transferOut: 'Transfer Out',
    internalTransfers: 'Internal Transfers',
    roles: 'Roles',
    hosting: 'Web Hosting',
    storage: 'Object Storage',
    kubernetes: 'Kubernetes',
    registry: 'Container Registry',
    vserver: 'VServer',
    adminStorage: 'Storage',
    adminKubernetes: 'Kubernetes',
    adminHosting: 'Web Hosting',
    adminRegistry: 'Container Registry',
    adminVServer: 'VServer',
    adminMail: 'Email'
  },
  mobileToggle: 'Toggle navigation',
  version: 'Version'
} as const

export const emailVerification = {
  bannerMessage: 'Your email address has not been verified yet. Some features are only available after verification.',
  resend: 'Resend email',
  sent: 'Email sent',
  successTitle: 'Email sent',
  successMessage: 'A verification email has been sent to your address.',
  alreadyVerifiedTitle: 'Already verified',
  alreadyVerifiedMessage: 'Your email address is already verified.',
  errorTitle: 'Error',
  blockingTitle: 'Email verification required',
  blockingMessage: 'Please verify your email address to use the dashboard. We have sent you a confirmation link.',
  checkInbox: 'Please check your inbox and click on the confirmation link.'
} as const

export const handleVerification = {
  expiredTitle: 'Verification expired',
  expiredMessage: 'Verification for {name} has expired. Domain registrations are not possible.',
  warningTitle: 'Verification expiring',
  warningMessage: 'Verification for {name} expires in {days} days.',
  infoTitle: 'Verification pending',
  infoMessage: '{name} is not fully verified (email and phone required).',
  action: 'Check contacts'
} as const

export const impersonation = {
  banner: {
    warning: 'You are acting as {name}',
    end: 'End impersonation'
  },
  started: {
    title: 'Impersonation started',
    message: 'You are now viewing the dashboard from the user\'s perspective.'
  },
  ended: {
    title: 'Impersonation ended',
    message: 'You are logged in as admin again.'
  },
  error: {
    noToken: 'No token available',
    blocked: 'This action is not permitted during impersonation.',
    failed: 'Impersonation failed'
  }
} as const

export const account = {
  title: 'Account',
  personalData: 'Personal Data',
  paymentMethods: 'Payment Methods',
  masterData: 'Master Data',
  manage: 'Manage',
  cancel: 'Cancel',
  accountSettings: 'Account Settings',
  loadingAccountData: 'Loading account data...',

  tabs: {
    details: 'Details',
    security: 'Security',
    payments: 'Payment Data',
    tokens: 'API Tokens',
    connections: 'Connected applications',
    sshKeys: 'SSH Keys',
    notifications: 'Notifications',
    roles: 'Roles & Access'
  },

  connections: {
    title: 'Connected applications',
    subtitle: 'AI agents and other applications you have given access to your account.',
    loadFailed: 'Your connected applications could not be loaded.',
    retry: 'Try again',
    empty: {
      title: 'No applications connected',
      hint: 'When you connect an AI agent to your ITSH account, it appears here. You see what it may do and what it has done, and you can disconnect it at any time.'
    },
    noVerifiedHost: 'No verified host: the application registered itself, and its name says nothing about who runs it.',
    status: {
      active: 'Active',
      revoked: 'Disconnected',
      expired: 'Expired'
    },
    noScopes: 'No permissions.',
    truncated: 'The latest {shown} of {total} applications.',
    spend: 'Spending',
    spendOf: '{spent} of at most {cap}',
    noSpending: 'May not spend',
    connected: 'Connected',
    connectedFrom: 'on {date} from {ip}',
    lastPresented: 'Last presented',
    transferConsent: 'Third-country transfer',
    transferConsentGiven: 'You consented',
    never: 'Never',
    expires: 'Valid until',
    noExpiry: 'No end date, until you disconnect it',
    ended: 'Ended',
    revokedReasons: {
      customer: 'disconnected by you',
      agent: 'disconnected by the application itself',
      admin: 'disconnected by ITSH support',
      account_closed: 'when the account was closed',
      code_replay: 'because a sign-in code was reused',
      refresh_reuse: 'because a token was reused',
      client_removed: 'because the application was removed',
      expired: 'expired',
      password_changed: 'because the account password was reset or changed',
      other: 'ended'
    },
    tools: {
      account_get: 'Show the account',
      subscription_list: 'List subscriptions',
      domain_list: 'List domains',
      domain_get: 'Show a domain',
      domain_search: 'Check whether a domain is available',
      domain_tld_catalogue: 'List domain endings',
      domain_tld_price: 'Show the price of a domain ending',
      dns_record_list: 'List DNS records',
      dns_record_create: 'Add a DNS record',
      dns_record_update: 'Change a DNS record',
      dns_record_delete: 'Delete a DNS record',
      mail_account_list: 'List email addresses',
      mail_account_get: 'Show an email address',
      mailbox_create: 'Create a mailbox',
      mail_forward_create: 'Create an alias',
      mail_account_update: 'Change a mailbox or alias',
      mail_account_delete: 'Delete a mailbox or alias',
      mailbox_password_link: 'Link to set a mailbox password',
      mail_vacation_get: 'Show a vacation reply',
      mail_vacation_set: 'Set a vacation reply',
      mail_catchall_get: 'Show the catch-all',
      mail_catchall_set: 'Set up the catch-all',
      mail_catchall_disable: 'Turn off the catch-all',
      mail_dns_status: 'Check the mail DNS records',
      mail_dns_apply: 'Add missing mail DNS records',
      mail_dns_replace: 'Replace the mail DNS records',
      handle_list: 'List domain contacts',
      handle_create: 'Add a domain contact',
      handle_update: 'Change a domain contact',
      handle_verify_request: 'Send a verification code',
      handle_verify_confirm: 'Confirm a verification code',
      handle_verify_status: 'Show a verification status',
      grant_get: 'Show this connection',
      grant_revoke: 'Disconnect itself'
    },
    showActivity: 'Show activity',
    hideActivity: 'Hide activity',
    disconnect: {
      action: 'Disconnect',
      title: 'Disconnect application',
      message: 'The application loses all access to your account at once.',
      warning: 'This cannot be undone. To use it again, connect it anew. Credentials it has already created keep working until you revoke them one by one.',
      confirm: 'Disconnect',
      working: 'Disconnecting',
      success: 'Application disconnected',
      failed: 'The application could not be disconnected'
    },
    activity: {
      loadFailed: 'The activity could not be loaded.',
      callsTitle: 'What the application did',
      callsHint: 'Recorded before it runs: every change, and anything beyond reading, such as fetching a credential. Reading alone is not listed.',
      callsEmpty: 'This application has not changed anything yet. When it last accessed your account is shown above under "Last presented".',
      directRequest: 'Direct request',
      outcomes: {
        attempted: 'In progress',
        ok: 'Done',
        client_error: 'Rejected',
        server_error: 'Failed',
        denied: 'Not allowed',
        unknown: 'Result unknown'
      },
      unknownHint: 'The request was cut off before it answered, so whether it changed anything is not known. Check the item it concerns.',
      previous: 'Before:',
      argsTruncated: 'Some values were shortened or left out to fit.',
      externalTargets: 'Asked to forward mail outside your domains to:',
      oversize: '{bytes} bytes, too long to keep',
      digest: 'SHA-256 checksum:',
      yes: 'yes',
      no: 'no',
      none: 'none',
      args: {
        domain: 'Domain',
        address: 'Address',
        id: 'ID',
        mailbox_id: 'Mailbox ID',
        channel: 'Channel',
        type: 'Type',
        name: 'Name',
        content: 'Content',
        ttl: 'TTL',
        priority: 'Priority',
        country: 'Country',
        active: 'Switched on',
        targets: 'Forwards to',
        enabled: 'Vacation reply on',
        from_date: 'From',
        to_date: 'Until',
        subject: 'Subject',
        message: 'Text',
        deleted_address: 'Deleted address',
        deleted_type: 'Deleted kind'
      },
      values: {
        channel: {
          email: 'Email',
          phone: 'Phone'
        },
        deleted_type: {
          mailbox: 'Mailbox',
          alias: 'Alias'
        }
      },
      refusedTitle: 'Refused requests',
      refusedHint: 'Requests we refused with this application\'s access, for 30 days.',
      refusedEmpty: 'No refused requests.',
      noRoute: '(no known address)',
      truncated: 'The latest {shown} of {total}.',
      needed: 'Needs: {scopes}',
      reasons: {
        scope: 'The application has no permission for this',
        session_only: 'Only possible in the control panel itself',
        unmapped: 'Not an address of the API',
        admin: 'ITSH administration area',
        audience: 'Access issued for a different interface',
        agent_access: 'Agents are not enabled for your account yet',
        other: 'Refused'
      }
    }
  },

  notifications: {
    title: 'Notifications',
    subtitle: 'Choose what we email you about.',
    saved: 'Settings saved.',
    vserverDown: {
      label: 'Email me if my server goes down unexpectedly',
      hint: 'We notify you as soon as your server goes offline unexpectedly.'
    },
    vserverDiskUsage: {
      label: "Email me when my server's disk is almost full",
      hint: 'You get a warning before your server runs out of disk space.'
    }
  },

  roles: {
    title: 'Roles & Access',
    subtitle: 'Your assigned roles and delegated domain access',
    loading: 'Loading roles...',
    yourRoles: 'Your Roles',
    noRoles: 'No roles assigned',
    noRolesDescription: 'You currently have no special roles.',
    delegatedAccess: 'Delegated Access',
    noDelegations: 'No delegated access',
    noDelegationsDescription: 'Other users have not granted you access to their domains.',
    delegatedBy: 'Delegated by',
    permissions: 'Permissions',
    grantedAt: 'Granted',
    expiresAt: 'Expires',
    never: 'Never',
    goToDomain: 'Go to domain'
  },

  avv: {
    navTitle: 'DPA',
    title: 'Data Processing Agreement',
    subtitle: 'Data protection agreement pursuant to Art. 28 GDPR',
    loading: 'Loading DPA status...',
    previewTitle: 'Please read and accept the Data Processing Agreement',
    acceptButton: 'Accept DPA',
    accepting: 'Accepting...',
    acceptedTitle: 'DPA accepted',
    acceptedInfo: 'Accepted on {date}',
    downloadButton: 'Download PDF',
    newVersionAvailable: 'A new version of the DPA is available',
    emailNotVerified: 'Please verify your email address first',
    holderOnly: 'Only the account holder can accept the Data Processing Agreement.',
    error: {
      loadFailed: 'Failed to load DPA status',
      acceptFailed: 'Failed to accept DPA',
      pdfFailed: 'The PDF could not be loaded',
      retry: 'Try again'
    }
  },

  terms: {
    navTitle: 'Terms',
    title: 'Terms and service agreements',
    loading: 'Loading status...',
    currentVersion: 'Current version of {date}',
    governingVersion: 'Your existing contracts are governed by the version of {date}.',
    acceptedInfo: 'You accepted the version of {version} on {date}.',
    nothingPending: 'No acceptance is pending for your contracts.',
    holderOnly: 'Only the account holder can accept, in their own session, not in a customer context or while a session is taken over.',
    fullText: 'Read the new version',
    comparison: 'View the comparison',
    savePdf: 'Save as PDF',
    summaryTitle: 'What changes',
    acceptedTitle: 'Acceptance saved',
    acceptedMessage: 'Your acceptance of the version of {date} is saved.',
    stale: 'A newer version has been published since. Reload the page to read it.',
    textUnavailable: 'This version of the portal does not carry the text of the current version yet. Reload the page or try again later.',
    reload: 'Reload page',
    emailNotVerified: 'Please verify your email address first.',
    accountInactive: 'Your account is deactivated. Please contact support.',
    error: {
      loadFailed: 'The status could not be loaded',
      acceptFailed: 'The acceptance could not be saved',
      retry: 'Try again'
    },
    // text and textWithoutOldVersion are the legal side's wording. text
    // interpolates {date} and {oldDate}; textWithoutOldVersion has only {date},
    // for contracts older than the first recorded version.
    banner: {
      text: 'New version of our terms and service agreements dated {date}. The version of {oldDate} continues to apply to your existing contracts until you accept the new one. Without acceptance, either side may terminate the contract under section 3.2 of the terms.',
      textWithoutOldVersion: "New version of our terms and service agreements dated {date}. The version you accepted when you ordered continues to apply to your existing contracts until you accept the new one. Without acceptance, either side may terminate the contract on one month's notice.",
      changesLink: 'View changes',
      acceptLink: 'Review and accept',
      avvText: 'Please accept the current Data Processing Agreement (DPA). We need it for your services in which we process personal data on your behalf.',
      avvAlso: "If we process third parties' personal data for you, for example in mailboxes or on your website, please also accept the current Data Processing Agreement (DPA)."
    },
    avv: {
      title: 'Data Processing Agreement (DPA)',
      text: 'For your services in which we process personal data on your behalf, we need your acceptance of the current version of the DPA. You read and accept it on its own page.',
      link: 'Read and accept the DPA'
    }
  },

  apiTokens: {
    title: 'API Tokens',
    subtitle: 'Manage your API tokens for external integrations',
    createToken: 'Create token',
    active: 'Active',
    inactive: 'Inactive',
    never: 'Never',
    copied: 'Copied',
    copy: 'Copy',

    columns: {
      name: 'Name',
      created: 'Created',
      lastUsed: 'Last used',
      status: 'Status',
      actions: 'Actions'
    },

    empty: {
      title: 'No API tokens',
      description: 'Create an API token to connect external services with your account.'
    },

    create: {
      title: 'Create API token',
      successTitle: 'Token created',
      name: 'Name',
      namePlaceholder: 'e.g. acme.sh production',
      nameHint: 'Choose a descriptive name to identify the token later.',
      warning: 'Save this token now - it will not be shown again.'
    },

    delete: {
      title: 'Delete token',
      message: 'Are you sure you want to delete this token?',
      warning: 'All applications using this token will lose access.',
      confirm: 'Delete'
    },

    notifications: {
      createSuccess: 'Token created',
      deleteSuccess: 'Token deleted',
      maxTokensReached: 'Maximum number of tokens reached'
    }
  },

  sshKeys: {
    title: 'SSH Keys',
    subtitle: 'Manage your public SSH keys for accessing your servers',
    createKey: 'Add key',
    active: 'Active',
    inactive: 'Inactive',

    columns: {
      name: 'Name',
      fingerprint: 'Fingerprint',
      created: 'Created',
      status: 'Status',
      actions: 'Actions'
    },

    empty: {
      title: 'No SSH keys',
      description: 'Add a public SSH key to log in to your servers.'
    },

    create: {
      title: 'Add SSH key',
      name: 'Name',
      namePlaceholder: 'e.g. my-laptop',
      nameHint: 'Choose a descriptive name to identify the key later.',
      publicKey: 'Public key',
      publicKeyPlaceholder: 'ssh-ed25519 AAAA...',
      publicKeyHint: 'Paste your full SSH public key (starts with e.g. ssh-ed25519 or ssh-rsa).',
      invalidKey: 'This does not look like a valid SSH public key.'
    },

    delete: {
      title: 'Delete key',
      message: 'Are you sure you want to delete this SSH key?',
      warning: 'Any access relying on this key will be lost.',
      confirm: 'Delete'
    },

    notifications: {
      createSuccess: 'SSH key added',
      deleteSuccess: 'SSH key deleted'
    }
  },

  stammdaten: {
    title: 'Personal Data',
    description: 'Manage your contact details and address information',
    edit: 'Edit',
    saveFailed: 'Failed to save personal data!',
    saveSuccess: 'Personal data saved successfully!',

    name: 'Name',
    organization: 'Organisation',
    organizationName: 'Organisation name',
    firstName: 'First name',
    lastName: 'Last name',
    address: 'Address',
    street: 'Street',
    streetAndNumber: 'Street and house number',
    addressSupplement: 'Address supplement',
    optional: 'Optional',
    postcode: 'Postcode',
    postalCode: 'Postcode',
    city: 'City',
    state: 'County',
    country: 'Country',
    contact: 'Contact',
    phone: 'Phone',
    phoneNumber: 'Phone number',
    email: 'Email',
    emailAddress: 'Email address',
    emailCannotChange: 'Email cannot be changed',
    vatId: 'VAT ID',
    vatIdHint: "We check the number in the EU Commission's VIES system and again every 30 days; for a number from another EU country together with company name and billing address, for a German one the number alone. If that changes your pricing basis, you get an e-mail. Details in the privacy policy.",
    language: 'Language',

    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving...',

    emailChange: {
      title: 'Change email address',
      currentEmail: 'Current email',
      newEmail: 'New email address',
      newEmailPlaceholder: "new{'@'}example.com",
      requestChange: 'Request change',
      verifyTitle: 'Verify email',
      verifyDescription: 'We have sent a confirmation code to {email}.',
      otpPlaceholder: 'Enter confirmation code',
      currentCodeEmail: 'Code sent to your current address {email}',
      currentCodeTotp: 'Code from your authenticator app',
      currentCodeHint: 'This confirms that the change comes from you.',
      verify: 'Verify',
      cancel: 'Cancel',
      logoutWarning: 'You will be logged out',
      logoutWarningText:
        'After confirmation, you will be automatically logged out and will need to log in again with your new email address.',
      success: 'Email address changed',
      successMessage: 'Please log in with your new email address.',
      errors: {
        invalidEmail: 'Please enter a valid email address',
        sameEmail: 'The new email address must be different from the current one',
        emailInUse: 'This email address is already in use',
        emailNotVerified: 'Your current email address must be verified first',
        invalidOtp: 'Invalid confirmation code',
        noPendingChange: 'No pending email change found',
        currentCodeInvalid: 'The code sent to your current address is not correct',
        currentTotpInvalid: 'The code from your authenticator app is not correct',
        changeExpired: 'This change has expired or had too many wrong codes. Please request it again.',
        mailUnavailable: 'We could not send the confirmation codes. Please try again later.',
        tooManyRequests: 'Too many requests. Please wait a moment.',
        generic: 'An error occurred. Please try again.'
      }
    }
  },

  payments: {
    title: 'Payment Methods',
    description: 'Manage your saved payment methods',
    loading: 'Loading payment methods...',
    empty: 'No payment methods',
    emptyHint: 'Add a payment method to pay your invoices.',
    addMethod: 'Add payment method',
    addingMethod: 'Adding...',
    add: 'Add',

    active: 'Active',
    revoked: 'Revoked',
    since: 'Since',

    iban: 'IBAN',
    ibanLabel: 'IBAN',
    cardNumber: 'Card number',
    cardHolder: 'Card holder',
    ibanPlaceholder: 'GB82 WEST 1234 5698 7654 32',
    bankDataLoadFailed: 'Failed to load bank data.',
    manualKey: 'Manual key:',
    accountHolder: 'Account holder',
    bankName: 'Bank',
    creditor: 'Payee',
    paypalAccount: 'PayPal account',

    sepaMandate: 'SEPA Direct Debit Mandate',
    mandateIntro: 'I hereby authorise,',
    mandateRecipient: 'the payee IT-Service Harting (Creditor Identifier: NL08ZZZ502057730000) to collect payments from my account with the IBAN',
    mandateBank: 'at the financial institution',
    mandateDirectDebit: 'by direct debit. At the same time, I instruct my financial institution to honour the direct debits drawn on my account by the payee.',
    mandateConsent: 'I grant the SEPA Direct Debit Mandate and instruct my financial institution to honour the direct debits.',
    mandateInfo: 'The advance notification period is reduced to 1 day. You are entitled to request a refund within 8 weeks from the debit date.',

    revokeTitle: 'Revoke payment method?',
    revokeDescription: 'Do you really want to revoke this payment method? You will no longer be able to make payments with this account after revocation.',
    revokeConfirm: 'Revoke',
    revokeMethod: 'Revoke payment method',
    cannotRevokeLastMethod: 'You cannot revoke the last active payment method.',
    cannotDeleteLast: 'This payment method cannot be removed while you have active subscriptions.',

    loadingStripe: 'Loading payment form...',
    validationError: 'Please check your input.',
    setupFailed: 'Failed to set up payment method.',
    unknownError: 'An unknown error occurred.',
    addedTitle: 'Payment method added',
    addedMessage: 'Your payment method has been added successfully.',
    securityNote: 'Your data is transmitted securely.',

    defaultMethod: 'Default',
    setDefault: 'Set as default',
    setDefaultSuccess: 'Payment method set as default.',

    cancel: 'Cancel',
    phoneRequiredTitle: 'Phone verification required',
    phoneRequired: 'Please verify your phone number under Contacts to add payment methods.'
  },

  security: {
    title: 'Security Settings',
    description: 'Manage your authentication and access credentials',

    twoFactor: 'Two-Factor Authentication',
    twoFactorDescription: 'Additional security for your account',
    twoFactorActive: 'Your account is protected by two-factor authentication.',
    twoFactorActivate: 'Enable two-factor authentication for additional protection. You will need an authenticator app such as Google Authenticator or Authy.',
    setupOtp: 'Set up OTP',
    preparing: 'Preparing...',
    activated: 'Activated',
    notActivated: 'Not activated',
    setupSuccess: 'Two-factor authentication has been set up successfully!',

    scanQrCode: 'Scan this QR code with your authenticator app',
    qrCodeAlt: 'QR code for 2FA',

    verificationCode: 'Verification code',
    verificationPlaceholder: '6-digit code',
    verificationHint: 'Enter the 6-digit code from your authenticator app',
    verify: 'Verify',
    verifying: 'Verifying...',
    cancel: 'Cancel',
    emailCode: 'Code from your email',
    emailCodeHint: 'We have emailed a code to your address. It is valid for 10 minutes.',
    totpAlreadyEnabled: 'Two-factor authentication is already set up for your account.',
    setupExpired: 'This setup has expired or had too many wrong codes. Please start again.',
    emailCodeInvalid: 'The code from the email is not correct.',
    totpInvalid: 'The code from your authenticator app is not correct.',
    mailUnavailable: 'We could not send the email code. Please try again later.',
    tooManyAttempts: 'Too many attempts. Please wait a few minutes and try again.',

    passwordChange: 'Change password',
    passwordChangeDescription: 'Update your account password',
    currentPassword: 'Current password',
    currentPasswordPlaceholder: 'Your current password',
    newPassword: 'New password',
    newPasswordPlaceholder: 'New password',
    confirmPassword: 'Confirm password',
    confirmPasswordPlaceholder: 'Repeat password',
    passwordsNoMatch: 'Passwords do not match',
    confirmationCode: 'Confirmation code',
    confirmationCodePlaceholder: '6-digit code',
    confirmationCodeHint: 'Enter the code from your authenticator app',
    passwordChanged: 'Password changed successfully!',
    passwordChangeFailed: 'Failed to change password',
    requestOtp: 'Request OTP',
    changePassword: 'Change password',
    processing: 'Processing...',

    sessions: {
      title: 'Active Sessions',
      description: 'Your currently active login sessions',
      current: 'Current session',
      revoke: 'Revoke',
      revokeConfirm: 'Revoke this session? The device will be logged out.',
      revokeSuccess: 'Session revoked',
      empty: 'No active sessions',
      unknownLocation: 'Unknown location',
      unknownDevice: 'Unknown device'
    }
  }
} as const

export const loading = {
  paymentMethods: 'Loading payment methods...'
} as const

export const empty = {
  paymentMethods: 'No payment methods',
  paymentMethodsHint: 'Add a payment method to pay your invoices.'
} as const

export const sepa = {
  addMethod: 'Add payment method',
  mandate: 'I hereby authorise...'
} as const

export const invoice = {
  title: 'Invoices',
  subtitle: 'Your invoices and orders',
  loading: 'Loading invoices...',
  allInvoices: 'All invoices',
  allOrders: 'All orders',
  ordersHint: 'These are your shop purchases. Renewals and monthly billing are under Invoices.',
  entries: '{count} entries',
  due: 'Due',
  pdfFilename: 'ITSH-Invoice',
  viewReceipt: 'Receipt',
  tabs: {
    invoices: 'Invoices',
    orders: 'Orders'
  },
  purpose: 'Purpose',
  period: 'Period: {start} to {end}',
  remaining: 'Outstanding: {amount}',
  downloadPdf: 'PDF',
  viewOnline: 'Online',
  linkError: 'The invoice cannot be opened right now. Please try again in a moment.',
  linkHolderOnly: 'Unpaid invoices can only be opened by the account holder, not during impersonation.',
  invoiceStatus: {
    paid: 'Paid',
    open: 'Open',
    void: 'Void',
    uncollectible: 'Unpaid'
  },
  stats: {
    invoices: 'Invoices',
    paid: 'Paid',
    open: 'Open',
    total: 'Total'
  },
  status: {
    paid: 'Paid',
    open: 'Open',
    refunded: 'Refunded',
    processing: 'Processing'
  },
  empty: {
    title: 'No invoices available',
    description: 'Once you place an order, your invoices will be displayed here.'
  },
  ordersEmpty: {
    title: 'No orders yet',
    description: 'Once you order something, it shows up here.'
  },
  estimate: {
    title: 'Estimated Costs',
    subtitle: 'Estimated costs for the current billing period',
    period: 'Period',
    category: {
      hosting: 'Hosting',
      domain: 'Domains',
      k8s: 'Kubernetes',
      s3: 'Object Storage',
      registry: 'Container Registry',
      vserver: 'VServer',
      mail: 'Email'
    },
    estimated: 'estimated',
    net: 'Net',
    vat: 'VAT 19%',
    reverseChargeNote: 'No VAT: reverse charge, you account for the VAT',
    notTaxableNote: 'No VAT: not taxable in Germany',
    total: 'Total',
    empty: 'No active subscriptions in this period',
    loading: 'Calculating estimate...',
    monthlyEstimate: 'Estimated costs'
  }
} as const

export const dashboardCommon = {
  loading: 'Loading...',
  noData: 'No data',
  noEntries: 'There are no entries.',
  confirm: 'Confirm',
  cancel: 'Cancel'
} as const
